import { ChangeEvent, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { ArrowDown, ArrowUp } from 'lucide-react';
import CustomButton from '../../components/CustomButton';
import {
    useAddOwnTermsDocumentMutation,
    useRemoveOwnTermsDocumentMutation,
    useReorderOwnTermsDocumentsMutation,
    useUpdateOwnTermsDocumentMutation,
} from '../../api/account/accountApiSlice';
import { TenantOwnTerms, TenantOwnTermsDocument } from '../../api/account/accountApiTypes';
import { enqueueErrorSnackbar, enqueueSuccessSnackbar } from '../../action/snackbarCommands';
import { errorMessageFrom } from '../../utils/errorMessage';
import { STATION_TIME_ZONE } from '../../utils/stationTime';

/** Mirrors `MAX_TERMS_PDF_BYTES` in new-backend/admin/src/lib/ownTerms.ts, which
 *  enforces it; checked here only so a too-large file fails before the upload. */
const MAX_TERMS_PDF_BYTES = 10 * 1024 * 1024;
/** Mirrors `MAX_TERMS_DOCUMENTS` there. */
const MAX_TERMS_DOCUMENTS = 10;

const isValidPdf = (file: File) => file.type === 'application/pdf' && file.size <= MAX_TERMS_PDF_BYTES;

const INPUT_CLASS = 'rounded-[5px] border border-foreground/15 px-3 py-2 text-sm text-foreground bg-transparent';

interface Props {
    ownTerms?: TenantOwnTerms;
    readOnly: boolean;
    /** A master on another tenant: its writes go through the by-slug routes (PO, 2026-10-30). */
    masterSlug?: string;
}

/** Which row has an inline form open, and for what. */
type Editing = { id: string, mode: 'link' | 'title' } | null;

/**
 * The tenant's own terms: one or more named documents — e.g. "AGB
 * Gästewohnungen", "Benutzerordnung", "Müllordnung" — each a PDF (recommended)
 * or a link to the tenant's own page (ADR-0003 amendment 2026-10-02).
 *
 * Several documents, because that is how tenants keep them: each part is
 * replaced on its own. Upload is recommended for a reason the tenant should
 * see: every uploaded version is kept with the date it went live, so the tenant
 * can later show which terms applied to a booking. A link cannot give that.
 *
 * Every write saves at once — the Services page has no unsaved-changes guard
 * (ADR-0005, 2026-09-12 amendment).
 */
const OwnTermsControl = ({ ownTerms, readOnly, masterSlug }: Props) => {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const [addDocument, { isLoading: adding }] = useAddOwnTermsDocumentMutation();
    const [updateDocument, { isLoading: updating }] = useUpdateOwnTermsDocumentMutation();
    const [removeDocument, { isLoading: removing }] = useRemoveOwnTermsDocumentMutation();
    const [reorderDocuments, { isLoading: reordering }] = useReorderOwnTermsDocumentsMutation();
    const [newTitle, setNewTitle] = useState('');
    const [newLink, setNewLink] = useState('');
    const [editing, setEditing] = useState<Editing>(null);
    const [editValue, setEditValue] = useState('');
    // Removal is confirmed inline: it takes the document off the live booking
    // page at once, and the last one leaves the page without own terms.
    const [confirmingRemove, setConfirmingRemove] = useState<string | null>(null);
    // The add form needs a title first. Its buttons stay clickable and say so,
    // rather than sitting greyed out: a disabled control explains nothing, and
    // on a phone it read as "the upload is broken" (PO, 2026-10-09).
    const [titleMissing, setTitleMissing] = useState(false);
    const [linkMissing, setLinkMissing] = useState(false);
    const titleRef = useRef<HTMLInputElement>(null);
    const linkRef = useRef<HTMLInputElement>(null);
    const hasTitle = newTitle.trim().length > 0;
    const requireTitle = () => {
        setTitleMissing(true);
        titleRef.current?.focus();
    };

    const documents = ownTerms?.documents ?? [];
    const pastVersions = ownTerms?.history.length ?? 0;
    const busy = adding || updating || removing || reordering;
    const canAdd = documents.length < MAX_TERMS_DOCUMENTS;

    const locale = i18n.language === 'de' ? 'de-DE' : 'en-GB';
    const formatDate = (iso: string) => {
        const date = new Date(iso);
        return Number.isNaN(date.getTime())
            ? t('common.not_applicable')
            : date.toLocaleDateString(locale, { timeZone: STATION_TIME_ZONE });
    };

    const run = async (action: () => Promise<unknown>, successKey: string) => {
        try {
            await action();
            dispatch(enqueueSuccessSnackbar({ message: t(successKey), autoHideDuration: 3000 }));
            return true;
        } catch (err) {
            dispatch(enqueueErrorSnackbar({
                message: errorMessageFrom(err, t('services.own_terms_error'), t),
                autoHideDuration: 6000,
            }));
            return false;
        }
    };

    const refuseFile = () => dispatch(enqueueErrorSnackbar({
        message: t('services.own_terms_file_invalid'),
        autoHideDuration: 6000,
    }));

    /** The chosen file, or null; resets the input so the same file fires again. */
    const takeFile = (event: ChangeEvent<HTMLInputElement>): File | null => {
        const file = event.target.files?.[0] ?? null;
        event.target.value = '';
        return file;
    };

    const handleAddFile = async (event: ChangeEvent<HTMLInputElement>) => {
        const file = takeFile(event);
        if (!file) return;
        if (!isValidPdf(file)) {
            refuseFile();
            return;
        }
        const saved = await run(
            () => addDocument({ title: newTitle.trim(), file, slug: masterSlug }).unwrap(),
            'services.own_terms_saved',
        );
        if (saved) setNewTitle('');
    };

    const handleAddLink = async () => {
        if (!hasTitle) {
            requireTitle();
            return;
        }
        if (!newLink.trim()) {
            setLinkMissing(true);
            linkRef.current?.focus();
            return;
        }
        const saved = await run(
            () => addDocument({ title: newTitle.trim(), url: newLink.trim(), slug: masterSlug }).unwrap(),
            'services.own_terms_saved',
        );
        if (saved) {
            setNewTitle('');
            setNewLink('');
        }
    };

    const handleReplaceFile = async (id: string, event: ChangeEvent<HTMLInputElement>) => {
        const file = takeFile(event);
        if (!file) return;
        if (!isValidPdf(file)) {
            refuseFile();
            return;
        }
        await run(() => updateDocument({ id, file, slug: masterSlug }).unwrap(), 'services.own_terms_saved');
    };

    const startEditing = (document: TenantOwnTermsDocument, mode: 'link' | 'title') => {
        setEditing({ id: document.id, mode });
        setEditValue(mode === 'title' ? document.title : '');
    };

    const handleSaveEdit = async () => {
        if (!editing) return;
        const value = editValue.trim();
        const saved = await run(
            () => updateDocument(editing.mode === 'title'
                ? { id: editing.id, title: value, slug: masterSlug }
                : { id: editing.id, url: value, slug: masterSlug }).unwrap(),
            'services.own_terms_saved',
        );
        if (saved) setEditing(null);
    };

    const move = (index: number, offset: -1 | 1) => {
        const ids = documents.map((document) => document.id);
        const [moved] = ids.splice(index, 1);
        ids.splice(index + offset, 0, moved);
        void run(() => reorderDocuments({ ids, slug: masterSlug }).unwrap(), 'services.own_terms_saved');
    };

    return (
        <div className={'flex flex-col gap-4 border-t border-foreground/5 pt-4 mt-4'}>
            <div>
                <h3 className={'text-sm font-semibold text-foreground'}>
                    {t('services.own_terms_title')}
                </h3>
                <p className={'text-sm text-foreground/60'}>
                    {t('services.own_terms_desc')}
                </p>
            </div>

            {documents.length === 0 ? (
                <p className={'text-sm font-medium text-foreground'}>{t('services.own_terms_none')}</p>
            ) : (
                <ol className={'flex flex-col divide-y divide-foreground/5 text-sm'}>
                    {documents.map((document, index) => (
                        <li key={document.id} className={'py-3 flex flex-col gap-2'}>
                            <div className={'flex items-start justify-between gap-3'}>
                                <div>
                                    <a
                                        href={document.url}
                                        target={'_blank'}
                                        rel={'noopener noreferrer'}
                                        className={'font-medium text-primary underline'}
                                    >
                                        {document.title}
                                    </a>
                                    <p className={'text-foreground/60'}>
                                        {document.kind === 'upload'
                                            ? t('services.own_terms_kind_upload', {
                                                fileName: document.fileName || t('services.own_terms_pdf'),
                                            })
                                            : t('services.own_terms_kind_link')}
                                        {' · '}
                                        {t('services.own_terms_since', { date: formatDate(document.since) })}
                                    </p>
                                </div>
                                {!readOnly && documents.length > 1 && (
                                    <div className={'flex gap-1'}>
                                        <button
                                            type={'button'}
                                            onClick={() => move(index, -1)}
                                            disabled={busy || index === 0}
                                            aria-label={t('services.own_terms_move_up', { title: document.title })}
                                            className={'p-1 text-foreground/60 disabled:opacity-30'}
                                        >
                                            <ArrowUp size={16} />
                                        </button>
                                        <button
                                            type={'button'}
                                            onClick={() => move(index, 1)}
                                            disabled={busy || index === documents.length - 1}
                                            aria-label={t('services.own_terms_move_down', { title: document.title })}
                                            className={'p-1 text-foreground/60 disabled:opacity-30'}
                                        >
                                            <ArrowDown size={16} />
                                        </button>
                                    </div>
                                )}
                            </div>

                            {!readOnly && (
                                <div className={'flex flex-wrap gap-x-4 gap-y-1'}>
                                    <label className={'text-sm font-medium text-primary cursor-pointer'
                                        + ' focus-within:underline'}
                                    >
                                        {t('services.own_terms_replace_pdf')}
                                        <input
                                            type={'file'}
                                            accept={'application/pdf'}
                                            onChange={(event) => handleReplaceFile(document.id, event)}
                                            disabled={busy}
                                            className={'sr-only'}
                                        />
                                    </label>
                                    <button
                                        type={'button'}
                                        onClick={() => startEditing(document, 'link')}
                                        disabled={busy}
                                        className={'text-sm font-medium text-primary'}
                                    >
                                        {t('services.own_terms_set_link')}
                                    </button>
                                    <button
                                        type={'button'}
                                        onClick={() => startEditing(document, 'title')}
                                        disabled={busy}
                                        className={'text-sm font-medium text-primary'}
                                    >
                                        {t('services.own_terms_rename')}
                                    </button>
                                    <button
                                        type={'button'}
                                        onClick={() => setConfirmingRemove(document.id)}
                                        disabled={busy}
                                        className={'text-sm font-medium text-foreground/70'}
                                    >
                                        {t('services.own_terms_remove')}
                                    </button>
                                </div>
                            )}

                            {confirmingRemove === document.id && (
                                <div className={'flex flex-col gap-2 border border-foreground/15 rounded-[5px] p-3'}>
                                    <p className={'text-sm text-foreground'} role={'alert'}>
                                        {documents.length === 1
                                            ? t('services.own_terms_remove_last_confirm')
                                            : t('services.own_terms_remove_confirm', { title: document.title })}
                                    </p>
                                    <div className={'flex flex-wrap gap-2'}>
                                        <CustomButton
                                            title={t('services.own_terms_remove')}
                                            onClick={async () => {
                                                await run(
                                                    () => removeDocument({ id: document.id, slug: masterSlug }).unwrap(),
                                                    'services.own_terms_removed',
                                                );
                                                setConfirmingRemove(null);
                                            }}
                                            variant={'delete'}
                                            isFilled={false}
                                            disabled={busy}
                                            loading={removing}
                                        />
                                        <CustomButton
                                            title={t('common.cancel')}
                                            onClick={() => setConfirmingRemove(null)}
                                            isFilled={false}
                                        />
                                    </div>
                                </div>
                            )}

                            {editing?.id === document.id && (
                                <div className={'flex flex-col sm:flex-row gap-2'}>
                                    <input
                                        type={editing.mode === 'link' ? 'url' : 'text'}
                                        value={editValue}
                                        onChange={(event) => setEditValue(event.target.value)}
                                        placeholder={editing.mode === 'link' ? t('services.own_terms_link_placeholder') : ''}
                                        aria-label={editing.mode === 'link'
                                            ? t('services.own_terms_link_label')
                                            : t('services.own_terms_title_label')}
                                        className={`flex-1 ${INPUT_CLASS}`}
                                    />
                                    <CustomButton
                                        title={t('common.save')}
                                        onClick={handleSaveEdit}
                                        isFilled={false}
                                        disabled={busy || !editValue.trim()}
                                        loading={updating}
                                    />
                                    <CustomButton
                                        title={t('common.cancel')}
                                        onClick={() => setEditing(null)}
                                        isFilled={false}
                                    />
                                </div>
                            )}
                        </li>
                    ))}
                </ol>
            )}

            {pastVersions > 0 && (
                <p className={'text-sm text-foreground/60'}>
                    {t('services.own_terms_history', { count: pastVersions })}
                </p>
            )}

            {!readOnly && !canAdd && (
                <p className={'text-sm text-foreground/60'}>
                    {t('api_errors.account_terms_too_many')}
                </p>
            )}

            {!readOnly && canAdd && (
                <div className={'flex flex-col gap-2 border-t border-foreground/5 pt-4'}>
                    <h4 className={'text-sm font-semibold text-foreground'}>
                        {t('services.own_terms_add')}
                    </h4>
                    <label htmlFor={'own-terms-title'} className={'text-sm text-foreground/70'}>
                        {t('services.own_terms_title_label')}
                    </label>
                    <input
                        id={'own-terms-title'}
                        ref={titleRef}
                        type={'text'}
                        value={newTitle}
                        onChange={(event) => {
                            setNewTitle(event.target.value);
                            setTitleMissing(false);
                        }}
                        placeholder={t('services.own_terms_title_placeholder')}
                        aria-invalid={titleMissing && !hasTitle}
                        className={INPUT_CLASS}
                    />
                    {titleMissing && !hasTitle && (
                        <p className={'text-sm text-foreground'} role={'alert'}>
                            {t('services.own_terms_title_required')}
                        </p>
                    )}
                    <div>
                        {hasTitle ? (
                            <label className={'text-sm font-medium text-primary cursor-pointer focus-within:underline'}>
                                {t('services.own_terms_upload')}
                                <input
                                    type={'file'}
                                    accept={'application/pdf'}
                                    onChange={handleAddFile}
                                    disabled={busy}
                                    className={'sr-only'}
                                />
                            </label>
                        ) : (
                            <button
                                type={'button'}
                                onClick={requireTitle}
                                className={'text-sm font-medium text-primary'}
                            >
                                {t('services.own_terms_upload')}
                            </button>
                        )}
                        <p className={'mt-1 text-xs text-foreground/60'}>
                            {t('services.own_terms_upload_hint')}
                        </p>
                    </div>
                    <label htmlFor={'own-terms-link'} className={'text-sm text-foreground/70'}>
                        {t('services.own_terms_link_label')}
                    </label>
                    <div className={'flex flex-col sm:flex-row gap-2'}>
                        <input
                            id={'own-terms-link'}
                            ref={linkRef}
                            type={'url'}
                            inputMode={'url'}
                            autoCapitalize={'none'}
                            value={newLink}
                            onChange={(event) => {
                                setNewLink(event.target.value);
                                setLinkMissing(false);
                            }}
                            placeholder={t('services.own_terms_link_placeholder')}
                            aria-invalid={linkMissing && !newLink.trim()}
                            className={`flex-1 ${INPUT_CLASS}`}
                        />
                        <CustomButton
                            title={t('services.own_terms_link_save')}
                            onClick={handleAddLink}
                            isFilled={false}
                            disabled={busy}
                            loading={adding}
                        />
                    </div>
                    {linkMissing && !newLink.trim() && (
                        <p className={'text-sm text-foreground'} role={'alert'}>
                            {t('services.own_terms_link_required')}
                        </p>
                    )}
                    <p className={'text-xs text-foreground/60'}>
                        {t('services.own_terms_link_hint')}
                    </p>
                </div>
            )}
        </div>
    );
};

export default OwnTermsControl;
